import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { paymentApi } from '../api/api';
import {
  Shield,
  Lock,
  Mail,
  FolderLock,
  Server,
  Check,
  Zap,
  EyeOff,
  Flag,
  ArrowRight,
  Clock,
  Star,
  Loader2,
  Sparkles,
  KeyRound,
  Globe,
  Fingerprint,
  Menu,
  X,
  MessageCircle,
  Video,
  Sun,
  Moon,
  Bell,
  FolderPlus,
  GripVertical,
  Calendar
} from 'lucide-react';

const REFERRAL_COOKIE_NAME = 'mpm_referral_code';
const REFERRAL_COOKIE_DAYS = 30;

// Cookie utility function
const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
};

// Matrix Rain Background Component - Optimized for mobile
const MatrixBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789@#$%アイウエオカキクケコ';
    const charArray = chars.split('');
    
    // Smaller font on mobile for performance
    const isMobile = window.innerWidth < 768;
    const fontSize = isMobile ? 12 : 16;
    const columns = Math.floor(canvas.width / fontSize);
    const drops = Array(columns).fill(0).map(() => Math.random() * -100);
    
    const draw = () => {
      ctx.fillStyle = 'rgba(10, 10, 35, 0.06)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      for (let i = 0; i < drops.length; i++) {
        const char = charArray[Math.floor(Math.random() * charArray.length)];
        const brightness = Math.random();
        
        if (brightness > 0.95) {
          ctx.fillStyle = 'rgba(120, 180, 255, 0.95)';
          ctx.shadowColor = 'rgba(100, 160, 255, 0.8)';
          ctx.shadowBlur = isMobile ? 5 : 10;
        } else if (brightness > 0.7) {
          ctx.fillStyle = 'rgba(80, 140, 255, 0.7)';
          ctx.shadowBlur = 0;
        } else {
          ctx.fillStyle = 'rgba(50, 100, 200, 0.5)';
          ctx.shadowBlur = 0;
        }
        
        ctx.font = `${fontSize}px monospace`;
        ctx.fillText(char, i * fontSize, drops[i] * fontSize);
        
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.98) {
          drops[i] = 0;
        }
        drops[i] += 0.4 + Math.random() * 0.4;
      }
    };
    
    // Slower interval on mobile for battery
    const interval = setInterval(draw, isMobile ? 50 : 40);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ background: 'linear-gradient(180deg, rgba(8,12,35,1) 0%, rgba(12,16,45,1) 50%, rgba(8,12,35,1) 100%)' }}
    />
  );
};

// Floating Particles - Reduced on mobile
const FloatingParticles = () => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const particleCount = isMobile ? 8 : 20;
  
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(particleCount)].map((_, i) => (
        <div
          key={i}
          className="absolute w-1 h-1 bg-[#6D4AFF] rounded-full opacity-40"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `float ${5 + Math.random() * 10}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`
          }}
        />
      ))}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.4; }
          50% { transform: translateY(-20px) translateX(10px); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

// Animated Gradient Border Card
const GlowCard = ({ children, className = '', featured = false }) => (
  <div className={`relative group ${className}`}>
    {featured && (
      <div className="absolute -inset-[1px] bg-gradient-to-r from-[#6D4AFF] via-[#9D7AFF] to-[#6D4AFF] rounded-2xl opacity-75 blur-sm group-hover:opacity-100 transition-opacity duration-500 animate-gradient-x" />
    )}
    <div className={`relative bg-[#0d0d1a] border ${featured ? 'border-[#6D4AFF]/50' : 'border-[#2d2d44]'} rounded-2xl overflow-hidden`}>
      {children}
    </div>
  </div>
);

export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Capture referral code from URL and store in cookie
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get('ref');
    if (refCode) {
      setCookie(REFERRAL_COOKIE_NAME, refCode, REFERRAL_COOKIE_DAYS);
      console.log(`Referral code ${refCode} stored in cookie for ${REFERRAL_COOKIE_DAYS} days`);
    }
  }, [location.search]);

  const features = [
    { icon: Lock, title: 'AES-256 Encryption', description: 'Military-grade encryption for every message. Your data stays yours.', gradient: 'from-blue-500/20 to-purple-500/20' },
    { icon: Shield, title: 'Closed Network', description: 'Only @mypatriotmail.org users can communicate. No outside threats.', gradient: 'from-purple-500/20 to-pink-500/20' },
    { icon: MessageCircle, title: 'Instant Messenger', description: 'Built-in chat with friends, online status, and emoji support.', gradient: 'from-cyan-500/20 to-blue-500/20' },
    { icon: Video, title: 'Secure Meetings', description: 'Schedule video calls with meet.saysme.org integration and calendar.', gradient: 'from-green-500/20 to-cyan-500/20' },
    { icon: FolderPlus, title: 'Custom Folders', description: 'Create unlimited folders to organize your encrypted communications.', gradient: 'from-orange-500/20 to-yellow-500/20' },
    { icon: GripVertical, title: 'Drag & Drop', description: 'Easily organize emails by dragging them to any folder.', gradient: 'from-pink-500/20 to-red-500/20' },
    { icon: Bell, title: 'Push Notifications', description: 'Never miss an email with real-time browser notifications.', gradient: 'from-indigo-500/20 to-purple-500/20' },
    { icon: Sun, title: 'Light & Dark Mode', description: 'Choose your preferred theme. Easy on the eyes, day or night.', gradient: 'from-yellow-500/20 to-orange-500/20' }
  ];

  const fallbackPlans = [
    { id: 'starter', name: 'Starter', monthly_price: 5, yearly_price: 50, description: 'For individuals', storage_limit_gb: 1, emails_per_day: 100, custom_folders_limit: 5, free_trial_enabled: false, is_featured: false },
    { id: 'patriot-pro', name: 'Patriot Pro', monthly_price: 19, yearly_price: 190, description: 'Most popular', storage_limit_gb: 50, emails_per_day: 500, custom_folders_limit: 25, free_trial_enabled: true, free_trial_days: 7, is_featured: true },
    { id: 'enterprise', name: 'Enterprise', monthly_price: 49, yearly_price: 490, description: 'For organizations', storage_limit_gb: 500, emails_per_day: 10000, custom_folders_limit: 100, free_trial_enabled: false, is_featured: false }
  ];

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const activePlans = await paymentApi.getActivePlans();
        setPlans(activePlans?.length > 0 ? activePlans : fallbackPlans);
      } catch (error) {
        console.error('Failed to fetch plans:', error);
        setPlans(fallbackPlans);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const formatPrice = (plan) => `$${billingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price}`;

  const getPlanFeatures = (plan) => {
    const features = [
      `${plan.storage_limit_gb} GB Storage`,
      `${plan.emails_per_day} Emails/Day`,
      `${plan.custom_folders_limit} Folders`,
      'E2E Encryption'
    ];
    if (plan.free_trial_enabled) features.push(`${plan.free_trial_days || 7}-Day Trial`);
    return features;
  };

  const securityFeatures = [
    { icon: EyeOff, text: 'Zero Knowledge' },
    { icon: Lock, text: 'E2E Encrypted' },
    { icon: Server, text: 'No Backdoors' },
    { icon: Flag, text: 'US Servers' }
  ];

  const stats = [
    { value: '256', label: 'Bit Encryption', suffix: '-bit' },
    { value: '0', label: 'Data Breaches', suffix: '' },
    { value: '100', label: 'Uptime', suffix: '%' },
    { value: '24/7', label: 'Protection', suffix: '' }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a18] text-white overflow-x-hidden">
      {/* Custom CSS */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Orbitron:wght@400;500;600;700;800;900&display=swap');
        
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 20px rgba(109, 74, 255, 0.4); }
          50% { box-shadow: 0 0 40px rgba(109, 74, 255, 0.8); }
        }
        
        @keyframes text-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 3s ease infinite;
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }
        
        .animate-text-shimmer {
          background: linear-gradient(90deg, #6D4AFF 0%, #9D7AFF 25%, #fff 50%, #9D7AFF 75%, #6D4AFF 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: text-shimmer 3s linear infinite;
        }
        
        .glass-card {
          background: rgba(13, 13, 26, 0.8);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(109, 74, 255, 0.1);
        }
        
        .cyber-grid {
          background-image: 
            linear-gradient(rgba(109, 74, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(109, 74, 255, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
        }
        
        /* Hide scrollbar but keep functionality */
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* Navigation - Mobile Optimized */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-card border-b border-[#2d2d44]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <div className="relative">
              <div className="absolute inset-0 bg-[#6D4AFF] rounded-lg sm:rounded-xl blur-md opacity-40" />
              <img 
                src="/mpm-logo.png" 
                alt="MyPatriotMail" 
                className="relative w-9 h-9 sm:w-11 sm:h-11 object-contain drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]"
              />
            </div>
            <span className="text-base sm:text-xl font-bold tracking-tight">MyPatriotMail</span>
          </div>
          
          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {['Features', 'Pricing', 'Security'].map((item) => (
              <a key={item} href={`#${item.toLowerCase()}`} className="text-gray-400 hover:text-white transition-colors">
                {item}
              </a>
            ))}
          </div>
          
          {/* Desktop Buttons */}
          <div className="hidden sm:flex items-center gap-2 sm:gap-4">
            <Button variant="ghost" onClick={() => navigate('/login')} className="text-gray-300 hover:text-white text-sm px-3">
              Sign In
            </Button>
            <Button onClick={() => navigate('/register')} className="bg-gradient-to-r from-[#6D4AFF] to-[#8B5FFF] rounded-full px-4 sm:px-6 text-sm shadow-[0_0_20px_rgba(109,74,255,0.4)]">
              Get Started
            </Button>
          </div>
          
          {/* Mobile Menu Button */}
          <button 
            className="sm:hidden p-2 text-gray-400 hover:text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        
        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="sm:hidden glass-card border-t border-[#2d2d44]/50 px-4 py-4 space-y-4">
            {['Features', 'Pricing', 'Security'].map((item) => (
              <a 
                key={item} 
                href={`#${item.toLowerCase()}`}
                className="block text-gray-400 hover:text-white py-2"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item}
              </a>
            ))}
            <div className="flex gap-3 pt-2">
              <Button variant="ghost" onClick={() => navigate('/login')} className="flex-1 text-gray-300">
                Sign In
              </Button>
              <Button onClick={() => navigate('/register')} className="flex-1 bg-gradient-to-r from-[#6D4AFF] to-[#8B5FFF] rounded-full">
                Get Started
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Mobile Optimized */}
      <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 pt-20 pb-8 overflow-hidden">
        <MatrixBackground />
        <FloatingParticles />
        
        {/* Gradient Orbs - Smaller on mobile */}
        <div className="absolute top-1/4 left-1/4 w-[300px] sm:w-[600px] h-[300px] sm:h-[600px] bg-[#6D4AFF] rounded-full blur-[150px] sm:blur-[200px] opacity-20 animate-pulse pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[200px] sm:w-[400px] h-[200px] sm:h-[400px] bg-[#9D7AFF] rounded-full blur-[100px] sm:blur-[150px] opacity-15 animate-pulse pointer-events-none" style={{ animationDelay: '1s' }} />
        
        <div className="max-w-6xl mx-auto text-center relative z-20">
          {/* Badge */}
          <div className="inline-flex items-center gap-1.5 sm:gap-2 glass-card rounded-full px-3 sm:px-5 py-2 mb-6 sm:mb-8 border border-[#6D4AFF]/30 animate-pulse-glow">
            <Zap className="w-3 h-3 sm:w-4 sm:h-4 text-[#6D4AFF]" />
            <span className="text-xs sm:text-sm text-gray-300">End-to-End Encrypted</span>
            <span className="hidden xs:inline w-1 h-1 sm:w-1.5 sm:h-1.5 bg-[#6D4AFF] rounded-full" />
            <span className="hidden xs:inline text-xs sm:text-sm text-gray-300">Internal Network</span>
          </div>
          
          {/* Main Headline - Responsive sizing */}
          <h1 className="text-4xl xs:text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black tracking-tighter mb-4 sm:mb-6 leading-[0.9]" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            <span className="block text-white drop-shadow-[0_0_20px_rgba(255,255,255,0.2)]">EMAIL FOR THE</span>
            <span className="block mt-1 sm:mt-2 animate-text-shimmer">FREE WORLD</span>
          </h1>
          
          {/* Subheadline */}
          <p className="text-sm sm:text-base md:text-lg text-gray-400 max-w-xs sm:max-w-lg md:max-w-2xl mx-auto mb-8 sm:mb-10 leading-relaxed px-2">
            Your data. Your business. <span className="text-white font-semibold">Nobody else's.</span>
            <span className="hidden sm:inline"><br />Military-grade encryption meets uncompromising privacy.</span>
          </p>
          
          {/* CTA Buttons - Stack on mobile */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-8 sm:mb-10 px-4">
            <Button 
              onClick={() => navigate('/register')}
              className="w-full sm:w-auto bg-gradient-to-r from-[#6D4AFF] via-[#8B5FFF] to-[#6D4AFF] bg-[length:200%_100%] animate-gradient-x rounded-full px-6 sm:px-10 py-5 sm:py-6 text-base sm:text-lg font-bold shadow-[0_0_30px_rgba(109,74,255,0.5)] hover:shadow-[0_0_50px_rgba(109,74,255,0.8)] transition-all group"
            >
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Start Free Trial
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/login')}
              className="w-full sm:w-auto bg-transparent border-2 border-[#2d2d44] hover:border-[#6D4AFF] text-white rounded-full px-6 sm:px-10 py-5 sm:py-6 text-base sm:text-lg font-medium transition-all"
            >
              <KeyRound className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Sign In
            </Button>
          </div>
          
          {/* Trust Badges - Horizontal scroll on mobile */}
          <div className="flex flex-wrap justify-center gap-3 sm:gap-6 text-xs sm:text-sm px-2">
            {[
              { icon: Clock, text: '7-Day Trial' },
              { icon: Shield, text: 'No Card Required' },
              { icon: Check, text: 'Cancel Anytime' }
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-1.5 sm:gap-2 text-gray-400">
                <div className="p-1 rounded-full bg-[#6D4AFF]/20">
                  <item.icon className="w-3 h-3 sm:w-4 sm:h-4 text-[#6D4AFF]" />
                </div>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
          
          {/* Stats - 2x2 grid on mobile */}
          <div className="mt-12 sm:mt-16 grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 px-2">
            {stats.map((stat, index) => (
              <div key={index} className="glass-card rounded-xl p-4 sm:p-6 border border-[#6D4AFF]/10">
                <div className="text-xl sm:text-2xl md:text-3xl font-black text-white" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                  {stat.value}{stat.suffix}
                </div>
                <div className="text-[10px] sm:text-xs text-gray-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Scroll Indicator - Hidden on small mobile */}
        <div className="hidden sm:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-2 animate-bounce">
          <span className="text-[10px] text-gray-500 uppercase tracking-widest">Scroll</span>
          <div className="w-5 h-8 border-2 border-[#6D4AFF]/30 rounded-full flex justify-center pt-1.5">
            <div className="w-1 h-2 bg-[#6D4AFF] rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Features Section - Mobile Optimized */}
      <section id="features" className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 relative">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-flex items-center gap-2 glass-card rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-6 border border-[#6D4AFF]/20">
              <Lock className="w-3 h-3 sm:w-4 sm:h-4 text-[#6D4AFF]" />
              <span className="text-xs sm:text-sm text-gray-400">Bank-Level Security</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight mb-3 sm:mb-4" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              <span className="text-white">FORT KNOX </span>
              <span className="bg-gradient-to-r from-[#6D4AFF] to-[#9D7AFF] bg-clip-text text-transparent">INBOX</span>
            </h2>
            <p className="text-gray-400 text-sm sm:text-base max-w-md mx-auto">
              Every feature built for <span className="text-white">absolute privacy</span>.
            </p>
          </div>
          
          {/* Features Grid - Single column on mobile */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {features.map((feature, index) => (
              <div key={index} className="group relative">
                <div className="glass-card rounded-xl sm:rounded-2xl p-5 sm:p-8 border border-[#2d2d44] hover:border-[#6D4AFF]/50 transition-all duration-300">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#6D4AFF]/20 to-[#9D7AFF]/20 rounded-xl flex items-center justify-center mb-4 sm:mb-6">
                    <feature.icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#6D4AFF]" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold mb-2 sm:mb-3">{feature.title}</h3>
                  <p className="text-gray-400 text-sm sm:text-base leading-relaxed">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's New Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#6D4AFF]/10 via-transparent to-[#9D7AFF]/10 pointer-events-none" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-flex items-center gap-2 glass-card rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-4 sm:mb-6 border border-[#6D4AFF]/20">
              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-[#6D4AFF]" />
              <span className="text-xs sm:text-sm text-gray-400">Latest Updates</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight mb-3 sm:mb-4" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              <span className="text-white">WHAT'S </span>
              <span className="bg-gradient-to-r from-[#00D4FF] to-[#6D4AFF] bg-clip-text text-transparent">NEW</span>
            </h2>
            <p className="text-gray-400 text-sm sm:text-base max-w-md mx-auto">
              Constantly evolving to keep you <span className="text-white">secure and connected</span>.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* My Patriot Wall - Featured */}
            <div 
              className="md:col-span-2 glass-card rounded-2xl p-6 sm:p-8 border border-emerald-500/30 hover:border-emerald-400/60 transition-all cursor-pointer group relative overflow-hidden"
              onClick={() => navigate('/wall-info')}
              data-testid="whats-new-wall-card"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/5 to-[#6D4AFF]/5 group-hover:from-emerald-500/10 group-hover:to-[#6D4AFF]/10 transition-all" />
              <div className="relative z-10 flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-[0_0_25px_rgba(16,185,129,0.3)]">
                    <Lock className="w-8 h-8 text-white" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs text-emerald-400 font-bold uppercase tracking-wider">Featured</span>
                    <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-500/30">New</span>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">My Patriot Wall</h3>
                  <p className="text-gray-400 text-sm leading-relaxed max-w-xl">
                    Your personal encrypted broadcast channel. Share updates, photos, and files with your subscribers. 
                    End-to-end encrypted, zero data mining — like your own private social network inside MyPatriotMail.
                  </p>
                  <div className="flex flex-wrap items-center gap-4 mt-3">
                    <span className="flex items-center gap-1 text-xs text-gray-500"><Check className="w-3.5 h-3.5 text-emerald-500" /> AES-256 Encrypted</span>
                    <span className="flex items-center gap-1 text-xs text-gray-500"><Check className="w-3.5 h-3.5 text-emerald-500" /> Media Uploads</span>
                    <span className="flex items-center gap-1 text-xs text-gray-500"><Check className="w-3.5 h-3.5 text-emerald-500" /> Push Notifications</span>
                    <span className="flex items-center gap-1 text-xs text-gray-500"><Check className="w-3.5 h-3.5 text-emerald-500" /> Custom URL</span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <ArrowRight className="w-6 h-6 text-emerald-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </div>

            {/* Instant Messenger Feature */}
            <div className="glass-card rounded-2xl p-6 sm:p-8 border border-[#2d2d44] hover:border-[#6D4AFF]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#6D4AFF] to-[#9D7AFF] rounded-xl flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-xs text-[#6D4AFF] font-bold uppercase">New Feature</span>
                  <h3 className="text-xl font-bold">Instant Messenger</h3>
                </div>
              </div>
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Add friends by searching their email
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  See who's online, away, or offline
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  100+ emojis for expressive chats
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Real-time message delivery
                </li>
              </ul>
            </div>

            {/* Video Meetings Feature */}
            <div className="glass-card rounded-2xl p-6 sm:p-8 border border-[#2d2d44] hover:border-[#6D4AFF]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#00D4FF] to-[#6D4AFF] rounded-xl flex items-center justify-center">
                  <Video className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-xs text-[#00D4FF] font-bold uppercase">New Feature</span>
                  <h3 className="text-xl font-bold">Secure Video Meetings</h3>
                </div>
              </div>
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Schedule meetings with calendar
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Auto-generated secure room links
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Invite multiple friends at once
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Powered by meet.saysme.org
                </li>
              </ul>
            </div>

            {/* Custom Folders Feature */}
            <div className="glass-card rounded-2xl p-6 sm:p-8 border border-[#2d2d44] hover:border-[#6D4AFF]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] rounded-xl flex items-center justify-center">
                  <FolderPlus className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-xs text-[#FF6B6B] font-bold uppercase">New Feature</span>
                  <h3 className="text-xl font-bold">Custom Folders</h3>
                </div>
              </div>
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Create personalized folders
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Choose custom colors
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Drag emails to organize
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Quick folder management
                </li>
              </ul>
            </div>

            {/* Theme & UI Feature */}
            <div className="glass-card rounded-2xl p-6 sm:p-8 border border-[#2d2d44] hover:border-[#6D4AFF]/50 transition-all">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#FFD93D] to-[#FF6B6B] rounded-xl flex items-center justify-center">
                  <Sun className="w-6 h-6 text-white" />
                </div>
                <div>
                  <span className="text-xs text-[#FFD93D] font-bold uppercase">Enhanced</span>
                  <h3 className="text-xl font-bold">Modern Interface</h3>
                </div>
              </div>
              <ul className="space-y-3 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Light & Dark theme toggle
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Auto-refresh every 5 minutes
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Push notifications
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Mark as unread filter
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Mobile Optimized */}
      <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#6D4AFF]/5 via-transparent to-[#6D4AFF]/5 pointer-events-none" />
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight mb-3" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              <span className="text-white">GET STARTED </span>
              <span className="bg-gradient-to-r from-[#00D4FF] to-[#6D4AFF] bg-clip-text text-transparent">FAST</span>
            </h2>
            <p className="text-gray-400 text-sm sm:text-base">Three simple steps</p>
          </div>
          
          {/* Steps - Vertical on mobile */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              { step: '01', title: 'Create Account', description: 'Choose your username. No personal info.', icon: Fingerprint },
              { step: '02', title: 'Auto Encrypt', description: 'Keys generated instantly.', icon: KeyRound },
              { step: '03', title: 'Start Messaging', description: 'Send encrypted emails.', icon: Mail }
            ].map((item, index) => (
              <div key={index} className="relative flex md:flex-col items-start md:items-center gap-4 md:gap-0 md:text-center">
                <div className="flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-[#6D4AFF] to-[#9D7AFF] rounded-xl flex items-center justify-center shadow-[0_0_30px_rgba(109,74,255,0.5)] md:mb-4">
                  <item.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                </div>
                <div className="flex-1 md:flex-none">
                  <div className="text-xs text-[#6D4AFF] font-bold mb-1">{item.step}</div>
                  <h3 className="text-base sm:text-lg font-bold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section - Mobile Optimized */}
      <section id="pricing" className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 relative">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight mb-3" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              <span className="text-white">SIMPLE </span>
              <span className="bg-gradient-to-r from-[#6D4AFF] to-[#FF6B6B] bg-clip-text text-transparent">PRICING</span>
            </h2>
            <p className="text-gray-400 text-sm sm:text-base mb-6 sm:mb-8">No hidden fees. Ever.</p>
            
            {/* Billing Toggle */}
            <div className="inline-flex items-center glass-card rounded-full p-1 border border-[#6D4AFF]/20">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-medium transition-all ${
                  billingCycle === 'monthly' ? 'bg-gradient-to-r from-[#6D4AFF] to-[#8B5FFF] text-white' : 'text-gray-400'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('yearly')}
                className={`px-4 sm:px-6 py-2 rounded-full text-xs sm:text-sm font-medium transition-all flex items-center gap-1.5 ${
                  billingCycle === 'yearly' ? 'bg-gradient-to-r from-[#6D4AFF] to-[#8B5FFF] text-white' : 'text-gray-400'
                }`}
              >
                Yearly
                <span className="bg-green-500/20 text-green-400 text-[10px] px-1.5 py-0.5 rounded-full">-17%</span>
              </button>
            </div>
          </div>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#6D4AFF] animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
              {plans.map((plan, index) => (
                <GlowCard key={plan.id || index} featured={plan.is_featured} className="h-full">
                  <div className="p-5 sm:p-6 md:p-8 h-full flex flex-col">
                    {plan.is_featured && (
                      <div className="absolute -top-px left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#6D4AFF] to-[#9D7AFF] text-white text-[10px] sm:text-xs font-bold px-3 py-1 rounded-b-lg flex items-center gap-1">
                        <Star className="w-3 h-3" /> POPULAR
                      </div>
                    )}
                    
                    <div className="mb-4 mt-2">
                      <h3 className="text-lg sm:text-xl font-bold mb-1">{plan.name}</h3>
                      <p className="text-gray-500 text-xs sm:text-sm">{plan.description}</p>
                    </div>
                    
                    <div className="mb-5 sm:mb-6">
                      <span className="text-3xl sm:text-4xl font-black" style={{ fontFamily: "'Orbitron', sans-serif" }}>{formatPrice(plan)}</span>
                      <span className="text-gray-500 text-sm ml-1">/{billingCycle === 'yearly' ? 'yr' : 'mo'}</span>
                    </div>
                    
                    <ul className="space-y-2 sm:space-y-3 mb-6 flex-grow">
                      {getPlanFeatures(plan).map((feature, fIndex) => (
                        <li key={fIndex} className="flex items-center gap-2 text-gray-300 text-sm">
                          <div className="w-4 h-4 rounded-full bg-[#6D4AFF]/20 flex items-center justify-center flex-shrink-0">
                            <Check className="w-2.5 h-2.5 text-[#6D4AFF]" />
                          </div>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <Button 
                      onClick={() => navigate('/register', { state: { planId: plan.id, billingCycle } })}
                      className={`w-full rounded-xl py-4 sm:py-5 font-bold text-sm sm:text-base transition-all ${
                        plan.is_featured 
                          ? 'bg-gradient-to-r from-[#6D4AFF] to-[#8B5FFF] hover:shadow-[0_0_30px_rgba(109,74,255,0.6)]' 
                          : 'bg-[#1a1a2e] hover:bg-[#252540] border border-[#2d2d44]'
                      }`}
                    >
                      Get Started
                    </Button>
                  </div>
                </GlowCard>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Security Section - Mobile Optimized */}
      <section id="security" className="py-16 sm:py-24 md:py-32 px-4 sm:px-6 relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="relative w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-8">
            <div className="absolute inset-0 bg-[#6D4AFF] rounded-full blur-xl opacity-30 animate-pulse" />
            <img 
              src="/mpm-logo.png" 
              alt="Security" 
              className="relative w-full h-full object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.4)]"
            />
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight mb-3" style={{ fontFamily: "'Orbitron', sans-serif" }}>
            <span className="text-white">PRIVACY </span>
            <span className="bg-gradient-to-r from-[#00D4FF] to-[#6D4AFF] bg-clip-text text-transparent">IRONCLAD</span>
          </h2>
          <p className="text-gray-400 text-sm sm:text-base mb-10 max-w-md mx-auto">
            Engineered into every line of code.
          </p>
          
          {/* Security Features Grid - 2x2 on mobile */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            {securityFeatures.map((item, index) => (
              <div key={index} className="glass-card rounded-xl p-4 sm:p-6 border border-[#2d2d44] hover:border-[#6D4AFF]/50 transition-all">
                <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 bg-gradient-to-br from-[#6D4AFF]/20 to-[#9D7AFF]/20 rounded-xl flex items-center justify-center">
                  <item.icon className="w-5 h-5 sm:w-6 sm:h-6 text-[#6D4AFF]" />
                </div>
                <p className="font-bold text-sm sm:text-base">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Mobile Optimized */}
      <section className="py-16 sm:py-24 md:py-32 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="relative glass-card rounded-2xl sm:rounded-3xl p-8 sm:p-12 md:p-16 text-center overflow-hidden border border-[#6D4AFF]/20">
            <div className="absolute inset-0 bg-gradient-to-r from-[#6D4AFF]/10 via-[#9D7AFF]/10 to-[#6D4AFF]/10 animate-gradient-x" />
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] sm:w-[600px] h-[150px] sm:h-[300px] bg-[#6D4AFF] rounded-full blur-[100px] sm:blur-[150px] opacity-15 pointer-events-none" />
            
            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 glass-card rounded-full px-3 sm:px-4 py-1.5 sm:py-2 mb-6 border border-[#6D4AFF]/30">
                <Globe className="w-3 h-3 sm:w-4 sm:h-4 text-[#6D4AFF]" />
                <span className="text-xs sm:text-sm text-gray-400">Join the Movement</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight mb-4 sm:mb-6" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                <span className="text-white">READY TO GO </span>
                <span className="animate-text-shimmer">DARK?</span>
              </h2>
              <p className="text-gray-400 text-sm sm:text-base mb-8 max-w-sm mx-auto">
                Join thousands of patriots who've taken back control.
              </p>
              <Button 
                onClick={() => navigate('/register')}
                className="w-full sm:w-auto bg-gradient-to-r from-[#6D4AFF] via-[#8B5FFF] to-[#6D4AFF] bg-[length:200%_100%] animate-gradient-x rounded-full px-8 sm:px-12 py-5 sm:py-6 text-base sm:text-lg font-bold shadow-[0_0_40px_rgba(109,74,255,0.5)] transition-all group"
              >
                Create Your Account
                <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className="text-gray-500 text-xs sm:text-sm mt-4 flex items-center justify-center gap-2">
                <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                7-day free trial • No card required
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - Mobile Optimized */}
      <footer className="py-10 sm:py-16 px-4 sm:px-6 border-t border-[#2d2d44]/50">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col items-center gap-6 sm:gap-8 text-center sm:text-left sm:flex-row sm:justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                src="/mpm-logo.png" 
                alt="MyPatriotMail" 
                className="w-9 h-9 sm:w-11 sm:h-11 object-contain drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]"
              />
              <span className="text-base sm:text-xl font-bold">MyPatriotMail</span>
            </div>
            
            <div className="flex flex-wrap justify-center gap-4 sm:gap-8 text-xs sm:text-sm text-gray-500">
              <button onClick={() => navigate('/privacy')} className="hover:text-[#6D4AFF] transition-colors">Privacy</button>
              <button onClick={() => navigate('/terms')} className="hover:text-[#6D4AFF] transition-colors">Terms</button>
              <button onClick={() => navigate('/roadmap')} className="hover:text-[#6D4AFF] transition-colors">Roadmap</button>
              <button onClick={() => navigate('/help')} className="hover:text-[#6D4AFF] transition-colors">Help & FAQ</button>
              <button className="hover:text-[#6D4AFF] transition-colors">Contact</button>
            </div>
            
            <p className="text-gray-600 text-xs sm:text-sm">
              © 2026 MyPatriotMail
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
